import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Snackbar,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import OrderDetail from '../components/OrderDetail';
import { uploadTempAttachments } from '../utils/attachmentUpload';
import OrderTypeSelector from '../components/OrderTypeSelector';
import AdvertisingOrderForm, { checkOrderTopSpotRequirements } from '../components/AdvertisingOrderForm';
import SchoolOrderForm from '../components/SchoolOrderForm';
import StationeryOrderForm from '../components/StationeryOrderForm';
import UpdateOrderForm from '../components/UpdateOrderForm';
import OrdersTabs from '../components/orders/OrdersTabs';
import PageHeader from '../components/common/PageHeader';
import { createSchoolOrder } from '../components/CreateSchoolOrder';
import useAdOrders from '../hooks/useAdOrders';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';

function Orders() {
  const { getToken, user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    organization_id: '',
    business_number: '',
    business_name: '',
    business_type: '',
    orderType: '',
    productType: '',
    notes: '',
    designNotes: '',
    // Invoice settings for ad orders
    invoiceLater: false,
    invoiceDate: '',
    // Stationery order fields
    quantity: '',
    size: '',
    stock: '',
    finish: '',
    price: '',
    freightPrice: '',
    additionalArtworkHours: '',
    margin: '',
    artworkFee: '',
    setupFee: '',
    gst: '',
    discountType: '$',
    discountAmount: '',
    totalPrice: '',
    audiences: [{
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: 1, // First audience is always Group 1
      startDate: '',
      endDate: '',
      price: '',
      discount: '0',
      discountType: '$', // Default to dollar discount
      eLinkType: 'None',
      eLinkContent: ''
    }]
  });
  const [topSpotConflicts, setTopSpotConflicts] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [newOrder, setNewOrder] = useState({
    business_number: '',
    business_name: '',
    business_type: '',
    orderType: '',
    audience: '',
    channel: '',
    productType: '',
    startDate: '',
    endDate: '',
    price: '',
    discount: '0',
    eLinkType: '',
    eLinkContent: '',
    artworkBrief: '',
    notes: '',
    designNotes: ''
  });
  const [organizations, setOrganizations] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [error, setError] = useState(null);
  const [schoolOrders, setSchoolOrders] = useState([]);
  const [stationeryOrders, setStationeryOrders] = useState([]);
  const [advertiserOrders, setAdvertiserOrders] = useState([]);
  const [updateOrders, setUpdateOrders] = useState([]);
  const [tabOrders, setTabOrders] = useState({
    all: [],
    school: [],
    ad: [],
    stationery: [],
    update: []
  });
  
  // Use the shared hook for ad orders
  const { adOrders, adOrdersLoading, adOrdersError, fetchAdOrders, updateOrderStatus } = useAdOrders();
  
  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchInitialData();
    }
  }, [isAuthenticated]);

  const fetchInitialData = async () => {
    try {
      const authToken = await getToken();
      
      // Fetch organizations
      const organizationsRes = await axios.get(`${config.apiUrl}/api/organizations`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // The API returns { success: true, data: [...], pagination: {...} }
      // We need to extract the data array
      if (organizationsRes.data && organizationsRes.data.success && Array.isArray(organizationsRes.data.data)) {
        setOrganizations(organizationsRes.data.data);
      } else if (Array.isArray(organizationsRes.data)) {
        // Fallback for legacy format
        setOrganizations(organizationsRes.data);
      } else {
        console.warn('Unexpected organizations response format:', organizationsRes.data);
        setOrganizations([]);
      }
      
      // Fetch orders
      await fetchOrders(authToken);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setOrganizations([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOrganizationChange = (e) => {
    const selectedOrg = organizations.find(org => org.id === e.target.value);
    if (selectedOrg) {
      // Extract values with priority for camelCase properties
      const businessNumber = selectedOrg.businessNumber || selectedOrg.business_number || '';
      const businessName = selectedOrg.name || selectedOrg.businessName || selectedOrg.business_name || '';
      const businessType = selectedOrg.businessType || selectedOrg.business_type || '';
      
      setFormData({
        ...formData,
        organization_id: selectedOrg.id,
        // Set both camelCase and snake_case versions for all fields to ensure compatibility
        businessNumber: businessNumber,
        business_number: businessNumber,
        businessName: businessName,
        business_name: businessName,
        businessType: businessType,
        business_type: businessType
      });
    }
  };

  // Function to calculate price using server-side API with weekly rates
  const calculatePriceWithDates = async (basePrice, channel, adType, startDate, endDate) => {
    if (!basePrice || !channel || !adType || !startDate || !endDate) {
      return basePrice;
    }
    
    try {
      // Get the auth token
      const authToken = await getToken();
      if (!authToken) {
        console.warn('No auth token available, using base price');
        return basePrice;
      }
      
      // Make direct API call with proper token
      const response = await fetch(`${config.apiUrl}/api/pricing/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          basePrice,
          channel,
          adType,
          startDate,
          endDate
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        return result.price;
      } else {
        console.warn('Price calculation failed, using base price:', result.message);
        return basePrice;
      }
    } catch (error) {
      console.error('Price calculation error:', error);
      return basePrice; // Fallback to base price
    }
  };

  const handleAudienceChange = (index, field, value) => {
    const newAudiences = [...formData.audiences];
    
    // Create updated audience with both camelCase and snake_case fields
    const updatedAudience = {
      ...newAudiences[index],
      [field]: value
    };
    
    // If the field uses snake_case, also add its camelCase equivalent
    if (field.includes('_')) {
      const camelField = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      updatedAudience[camelField] = value;
    }
    // If the field uses camelCase, also add its snake_case equivalent
    else if (/[A-Z]/.test(field)) {
      const snakeField = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      updatedAudience[snakeField] = value;
    }
    
    // If this is an organization_id change, calculate price based on current channel, ad type, and dates
    if ((field === 'organization_id' || field === 'organizationId') && value) {
      const selectedOrg = organizations.find(org => org.id === value);
      if (selectedOrg && selectedOrg.price) {
        const basePrice = parseFloat(selectedOrg.price);
        
        // If we have channel, adType, and dates, apply the weekly calculation
        const channel = updatedAudience.channel || updatedAudience.channel_field;
        const adType = updatedAudience.adType || updatedAudience.ad_type;
        const startDate = updatedAudience.startDate || updatedAudience.start_date;
        const endDate = updatedAudience.endDate || updatedAudience.end_date;
        
        if (channel && adType && startDate && endDate) {
          // Set fallback price immediately, then update with calculated price
          updatedAudience.price = basePrice.toString();
          updatedAudience.price_field = basePrice.toString();
          
          // Calculate the actual price asynchronously
          calculatePriceWithDates(basePrice, channel, adType, startDate, endDate)
            .then(calculatedPrice => {
              setFormData(currentFormData => {
                const currentAudiences = [...currentFormData.audiences];
                if (currentAudiences[index]) {
                  currentAudiences[index] = {
                    ...currentAudiences[index],
                    price: calculatedPrice.toString(),
                    price_field: calculatedPrice.toString()
                  };
                }
                return {
                  ...currentFormData,
                  audiences: currentAudiences
                };
              });
            })
            .catch(error => {
              console.error('Error calculating price:', error);
            });
        } else {
          // Fallback to base price if dates are missing
          updatedAudience.price = basePrice.toString();
          updatedAudience.price_field = basePrice.toString();
        }
      }
    }
    
    // If this is a channel, adType, or date change, recalculate price if organization is set
    if ((field === 'channel' || field === 'adType' || field === 'startDate' || field === 'endDate' || 
         field === 'channel_field' || field === 'ad_type' || field === 'start_date' || field === 'end_date') && 
        updatedAudience.organization_id) {
      const selectedOrg = organizations.find(org => org.id === updatedAudience.organization_id);
      if (selectedOrg && selectedOrg.price) {
        const basePrice = parseFloat(selectedOrg.price);
        
        // Get current values for channel, adType, and dates
        let channel = updatedAudience.channel || updatedAudience.channel_field;
        let adType = updatedAudience.adType || updatedAudience.ad_type;
        let startDate = updatedAudience.startDate || updatedAudience.start_date;
        let endDate = updatedAudience.endDate || updatedAudience.end_date;
        
        // Update with the new value if that's what changed
        if (field === 'channel' || field === 'channel_field') {
          channel = value;
        } else if (field === 'adType' || field === 'ad_type') {
          adType = value;
        } else if (field === 'startDate' || field === 'start_date') {
          startDate = value;
        } else if (field === 'endDate' || field === 'end_date') {
          endDate = value;
        }
        
        if (channel && adType && startDate && endDate) {
          // Set fallback price immediately, then update with calculated price
          updatedAudience.price = basePrice.toString();
          updatedAudience.price_field = basePrice.toString();
          
          // Calculate the actual price asynchronously
          calculatePriceWithDates(basePrice, channel, adType, startDate, endDate)
            .then(calculatedPrice => {
              setFormData(currentFormData => {
                const currentAudiences = [...currentFormData.audiences];
                if (currentAudiences[index]) {
                  currentAudiences[index] = {
                    ...currentAudiences[index],
                    price: calculatedPrice.toString(),
                    price_field: calculatedPrice.toString()
                  };
                }
                return {
                  ...currentFormData,
                  audiences: currentAudiences
                };
              });
            })
            .catch(error => {
              console.error('Error calculating price:', error);
            });
        }
      }
    }
    
    // Apply the update
    newAudiences[index] = updatedAudience;
    
    setFormData({
      ...formData,
      audiences: newAudiences
    });
  };

  const addAudience = () => {
    // Get values from the first audience if it exists
    const firstAudience = formData.audiences[0] || {};
    
    // Ensure first audience has adGroup set to 1 if not already set
    const updatedAudiences = [...formData.audiences];
    if (updatedAudiences[0] && !updatedAudiences[0].adGroup) {
      updatedAudiences[0] = { ...updatedAudiences[0], adGroup: 1 };
    }
    
    // Create a new audience with auto-populated fields from the first audience
    const newAudience = {
      organization_id: '', // Don't copy organization
      channel: '', // Don't copy channel
      adType: '', // Don't copy ad type
      adGroup: null, // Will be auto-assigned when ad type is selected
      startDate: firstAudience.startDate || '', // Copy start date if available
      endDate: firstAudience.endDate || '', // Copy end date if available
      price: '', // Don't copy price
      discount: '0',
      discountType: '$', // Default to dollar discount
      eLinkType: firstAudience.eLinkType || 'None', // Copy eLink type or default to None
      eLinkContent: firstAudience.eLinkType !== 'None' ? (firstAudience.eLinkContent || '') : '' // Copy eLink content if type isn't None
    };
    
    setFormData({
      ...formData,
      audiences: [
        ...updatedAudiences,
        newAudience
      ]
    });
  };

  const removeAudience = (index) => {
    const newAudiences = formData.audiences.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      audiences: newAudiences
    });
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setOpenDeleteDialog(true);
  };

  const handleDeleteOrder = async () => {
    try {
      // Get token for the request
      const authToken = await getToken();
      
      // Make API call to archive the order (not delete)
      const response = await axios.put(`${config.apiUrl}/api/orders/${orderToDelete.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.status === 200 || response.data.success) {
        // Filter out the archived order from local state
        setOrders(orders.filter(o => o.id !== orderToDelete.id));
        setSnackbar({
          open: true,
          message: 'Order archived successfully',
          severity: 'success'
        });
      }
      
      // Close dialog and reset
      setOpenDeleteDialog(false);
      setOrderToDelete(null);
    } catch (error) {
      console.error('Error archiving order:', error);
      
      setSnackbar({
        open: true,
        message: 'Failed to archive order',
        severity: 'error'
      });
      
      setOpenDeleteDialog(false);
      setOrderToDelete(null);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'prospect':
        return 'Prospect';
      case 'qualified':
        return 'Qualified';
      case 'proposal':
        return 'Proposal';
      case 'negotiation':
        return 'Negotiation';
      case 'closed_won':
        return 'Closed (Won)';
      case 'closed_lost':
        return 'Closed (Lost)';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'prospect':
        return '#9e9e9e'; // Grey
      case 'qualified':
        return '#2196f3'; // Blue
      case 'proposal':
        return '#ff9800'; // Orange
      case 'negotiation':
        return '#673ab7'; // Deep Purple
      case 'closed_won':
        return '#4caf50'; // Green
      case 'closed_lost':
        return '#f44336'; // Red
      default:
        return '#9e9e9e'; // Grey
    }
  };

  const groupOrdersByStatus = () => {
    const groups = {
      prospect: [],
      qualified: [],
      proposal: [],
      negotiation: [],
      closed_won: [],
      closed_lost: []
    };
    
    // Filter orders based on status filter, and ensure orders is always an array
    const ordersArray = Array.isArray(orders) ? orders : [];
    const filteredOrders = statusFilter === 'all'
      ? ordersArray
      : ordersArray.filter(order => order.status === statusFilter);
    
    // Group orders by status
    filteredOrders.forEach(order => {
      if (groups[order.status]) {
        groups[order.status].push(order);
      }
    });
    
    return groups;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOrderClick = async (order) => {
    try {
      
      // Get token for the request
      const authToken = await getToken();
      
      // Determine the correct endpoint based on order type
      let endpoint = `${config.apiUrl}/api/orders/${order.id}`; // default
      
      if (order.orderType === 'stationery') {
        endpoint = `${config.apiUrl}/api/stationery-orders/${order.id}`;
      } else if (order.orderType === 'ad') {
        endpoint = `${config.apiUrl}/api/ad-orders/${order.id}`;
      } else if (order.orderType === 'school') {
        endpoint = `${config.apiUrl}/api/school-orders/${order.id}`;
      }
      
      // Fetch the complete order details from the server
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      
      // Process the data to ensure both camelCase and snake_case properties exist
      // Handle both old and new response formats
      let orderData;
      if (response.data && response.data.hasOwnProperty('success') && response.data.hasOwnProperty('data')) {
        orderData = response.data.data;
      } else {
        orderData = response.data;
      }
      
      // Debug: Log the raw data for stationery orders
      
      // Helper function to normalize data with both camelCase and snake_case versions
      const normalizeField = (camelKey, snakeKey) => {
        const camelValue = orderData[camelKey];
        const snakeValue = orderData[snakeKey];
        const value = camelValue || snakeValue || '';
        return { camelKey, snakeKey, value };
      };
      
      // Create a processed order with normalized field values
      const processedOrder = {
        ...orderData,
        
        // Handle key fields that might exist in different case formats
        businessNumber: normalizeField('businessNumber', 'business_number').value,
        business_number: normalizeField('businessNumber', 'business_number').value,
        
        businessName: normalizeField('businessName', 'business_name').value,
        business_name: normalizeField('businessName', 'business_name').value,
        
        businessType: normalizeField('businessType', 'business_type').value,
        business_type: normalizeField('businessType', 'business_type').value,
        
        orderType: normalizeField('orderType', 'order_type').value,
        order_type: normalizeField('orderType', 'order_type').value,
        
        productType: normalizeField('productType', 'product_type').value,
        product_type: normalizeField('productType', 'product_type').value,
        
        designNotes: normalizeField('designNotes', 'design_notes').value,
        design_notes: normalizeField('designNotes', 'design_notes').value
      };
      
      // Process any audiences if present
      if (Array.isArray(processedOrder.audiences)) {
        processedOrder.audiences = processedOrder.audiences.map(audience => {
          // Normalize each audience field with both naming conventions
          return {
            ...audience,
            organization_id: audience.organization_id || audience.organizationId || '',
            organizationId: audience.organizationId || audience.organization_id || '',
            
            startDate: audience.startDate || audience.start_date || '',
            start_date: audience.startDate || audience.start_date || '',
            
            endDate: audience.endDate || audience.end_date || '',
            end_date: audience.endDate || audience.end_date || '',
            
            eLinkType: audience.eLinkType || audience.e_link_type || '',
            e_link_type: audience.eLinkType || audience.e_link_type || '',
            
            eLinkContent: audience.eLinkContent || audience.e_link_content || '',
            e_link_content: audience.eLinkContent || audience.e_link_content || '',
            
            // Ensure adType is preserved with both naming conventions
            adType: audience.adType || audience.ad_type || '',
            ad_type: audience.adType || audience.ad_type || ''
          };
        });
      }
      
      // Set the selected order with complete details
      setSelectedOrder(processedOrder);
      setOpenDetailDialog(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setSnackbar({
        open: true,
        message: `Error loading order details: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      // Determine order type
      const orderToUpdate = tabOrders.all.find(order => order.id === orderId);
      
      if (!orderToUpdate) {
        console.error(`Order with ID ${orderId} not found`);
        return;
      }
      
      const orderType = orderToUpdate.orderType || orderToUpdate.order_type;
      
      
      if (orderType === 'ad') {
        // Use our shared hook for ad orders
        const result = await updateOrderStatus(orderId, newStatus, '');
        
        // If status is cancelled, also update local state to show inactive
        if (newStatus.toLowerCase() === 'cancelled') {
          setTabOrders(prevTabOrders => ({
            ...prevTabOrders,
            all: prevTabOrders.all.map(order => 
              order.id === orderId 
                ? { ...order, status: newStatus, active: false } 
                : order
            ),
            ad: prevTabOrders.ad.map(order => 
              order.id === orderId 
                ? { ...order, status: newStatus, active: false } 
                : order
            )
          }));
        }
      } else if (orderType === 'stationery') {
        // Handle stationery orders
        const authToken = await getToken();
        const { fetchCSRFToken: fetchCSRF2 } = await import('../utils/csrfProtection');
        const csrfToken2 = await fetchCSRF2();
        
        const response = await axios.patch(
          `${config.apiUrl}/api/stationery-orders/${orderId}/status`,
          { status: newStatus, notes: '' },
          {
            headers: { Authorization: `Bearer ${authToken}`, 'X-CSRF-Token': csrfToken2 }
          }
        );
        
        // Update local state
        setTabOrders(prevTabOrders => ({
          ...prevTabOrders,
          all: prevTabOrders.all.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatus } 
              : order
          ),
          stationery: prevTabOrders.stationery.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatus } 
              : order
          )
        }));
      } else {
        // Handle other order types as before
        const authToken = await getToken();
        const { fetchCSRFToken: fetchCSRF3 } = await import('../utils/csrfProtection');
        const csrfToken3 = await fetchCSRF3();
        const endpoint = orderType === 'school' ? 'school-orders' : 'orders';
        
        const response = await axios.put(
          `${config.apiUrl}/api/${endpoint}/${orderId}/status`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${authToken}`, 'X-CSRF-Token': csrfToken3 } }
        );
        
        if (response.data.success) {
          // Update local state
          const updatedOrders = tabOrders.all.map(order => 
            order.id === orderId ? { ...order, status: newStatus } : order
          );
          
          // Re-categorize orders
          const categorized = categorizeOrders(updatedOrders);
          setTabOrders(categorized);
          
          setSnackbar({
            open: true,
            message: `Order status updated to ${newStatus}`,
            severity: 'success'
          });
        }
      }
      
      // Refresh orders to ensure consistency
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      setSnackbar({
        open: true,
        message: `Failed to update status: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleOrderUpdate = async (updatedOrder) => {
    try {
      // Check if this is a deletion notification
      if (updatedOrder === 'deleted') {
        // Refresh the orders list
        fetchOrders();
        return;
      }
      
      // Handle case where updatedOrder is a response object with success message only
      if (updatedOrder && updatedOrder.success === true && updatedOrder.message) {
        setSnackbar({
          open: true,
          message: updatedOrder.message,
          severity: 'success'
        });
        // Refresh the orders list
        fetchOrders();
        return;
      }
      
      // Make sure we have a valid order ID
      if (!updatedOrder || !updatedOrder.id) {
        console.error('Cannot update order: Missing order ID', updatedOrder);
        setSnackbar({
          open: true,
          message: 'Error: Cannot update order without a valid ID',
          severity: 'error'
        });
        return;
      }
      
      // Use the appropriate endpoint based on order type
      let endpoint;
      if (updatedOrder.orderType === 'school') {
        endpoint = `${config.apiUrl}/api/school-orders/${updatedOrder.id}`;
      } else if (updatedOrder.orderType === 'ad' || updatedOrder.orderType === 'advertising') {
        endpoint = `${config.apiUrl}/api/ad-orders/${updatedOrder.id}`;
      } else {
        endpoint = `${config.apiUrl}/api/orders/${updatedOrder.id}`;
      }
      
      // Prepare the data to match server expectations
      let requestData = { ...updatedOrder };
      
      // For ad orders, make sure to use the right field names expected by the server
      if (updatedOrder.orderType === 'ad' || updatedOrder.orderType === 'advertising') {
        requestData.organization = updatedOrder.organization_id || updatedOrder.organizationId;
      }
      
      // Get token for the request
      const authToken = await getToken();
      
      const response = await axios.put(
        endpoint,
        requestData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Order updated successfully',
          severity: 'success'
        });
        
        // Refresh orders list to show the updated order
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: `Error updating order: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Helper function to determine if an order is a school order
  const isSchoolOrder = (order) => {
    // Check only the explicit orderType field
    const orderType = (order.orderType || order.order_type || '').toLowerCase();
    return orderType === 'school';
  };

  const fetchOrders = async (providedToken = null) => {
    try {
      setLoading(true);
      const authToken = providedToken || await getToken();
      
      // Fetch school orders
      const schoolOrdersRes = await axios.get(`${config.apiUrl}/api/school-orders`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Check for the new response format with success and data properties
      if (schoolOrdersRes.data && schoolOrdersRes.data.success && Array.isArray(schoolOrdersRes.data.data)) {
        // Successfully fetched school orders
        setSchoolOrders(schoolOrdersRes.data.data);
      } else if (schoolOrdersRes.data && Array.isArray(schoolOrdersRes.data)) {
        // Fallback for old format (direct array)
                  // Successfully fetched school orders (legacy format)
        setSchoolOrders(schoolOrdersRes.data);
      } else {
        console.warn('Unexpected school orders data format:', schoolOrdersRes.data);
        setSchoolOrders([]);
      }
      
      // Try to fetch ad orders directly first, then fallback to mock if needed
      try {
        const adOrdersRes = await axios.get(`${config.apiUrl}/api/ad-orders`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (adOrdersRes.data && adOrdersRes.data.success && Array.isArray(adOrdersRes.data.data)) {
          // Successfully fetched ad orders
          setAdvertiserOrders(adOrdersRes.data.data);
        } else if (adOrdersRes.data && Array.isArray(adOrdersRes.data)) {
                      // Successfully fetched ad orders (legacy format)
          setAdvertiserOrders(adOrdersRes.data);
        } else {
          throw new Error('Unexpected ad orders data format');
        }
      } catch (adError) {
        console.warn('Error fetching real ad orders, trying mock endpoint:', adError);
        
        // Try the mock endpoint as fallback
        try {
          const mockResponse = await axios.get(`${config.apiUrl}/api/ad-orders/mock`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          if (mockResponse.data && mockResponse.data.success && Array.isArray(mockResponse.data.data)) {
            setAdvertiserOrders(mockResponse.data.data);
          } else {
            console.warn('Unexpected mock ad orders data format:', mockResponse.data);
            setAdvertiserOrders([]);
          }
        } catch (mockError) {
          console.error('Mock endpoint also failed:', mockError);
          setAdvertiserOrders([]);
        }
      }
      
      // Fetch stationery orders
      try {
        const stationeryOrdersRes = await axios.get(`${config.apiUrl}/api/stationery-orders`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (stationeryOrdersRes.data && stationeryOrdersRes.data.success && Array.isArray(stationeryOrdersRes.data.data)) {
          // Successfully fetched stationery orders
          setStationeryOrders(stationeryOrdersRes.data.data);
        } else if (stationeryOrdersRes.data && Array.isArray(stationeryOrdersRes.data)) {
          // Successfully fetched stationery orders (legacy format)
          setStationeryOrders(stationeryOrdersRes.data);
        } else {
          console.warn('Unexpected stationery orders data format:', stationeryOrdersRes.data);
          setStationeryOrders([]);
        }
      } catch (stationeryError) {
        console.warn('Error fetching stationery orders:', stationeryError);
        setStationeryOrders([]);
      }
      
      // Fetch update orders
      try {
        const updateOrdersRes = await axios.get(`${config.apiUrl}/api/update-orders`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (updateOrdersRes.data && updateOrdersRes.data.success && Array.isArray(updateOrdersRes.data.data)) {
          // Successfully fetched update orders
          setUpdateOrders(updateOrdersRes.data.data);
        } else if (updateOrdersRes.data && Array.isArray(updateOrdersRes.data)) {
          // Successfully fetched update orders (legacy format)
          setUpdateOrders(updateOrdersRes.data);
        } else {
          console.warn('Unexpected update orders data format:', updateOrdersRes.data);
          setUpdateOrders([]);
        }
      } catch (updateError) {
        console.warn('Error fetching update orders:', updateError);
        setUpdateOrders([]);
      }
      
      // Combine orders from different sources
      const allOrders = [
        ...schoolOrders,
        ...advertiserOrders,
        ...stationeryOrders,
        ...updateOrders
      ];
      
      // Categorize orders
      const categorized = categorizeOrders(allOrders);
      setTabOrders(categorized);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(`Failed to fetch orders: ${error.message || 'Unknown error'}`);
      setSnackbar({
        open: true,
        message: `Failed to fetch orders: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Update the categorizeOrders function to use advertiserOrders instead of adOrders
  const categorizeOrders = (ordersData) => {
    // Filter out school orders
    const schoolOrdersData = ordersData.filter(order => 
      order.orderType === 'school' || 
      (order.order_type === 'school')
    );
    
    // Use advertiserOrders instead of adOrders
    const adOrdersData = advertiserOrders;
    
    // Use stationeryOrders from state
    const stationeryOrdersData = stationeryOrders;
    
    // Combine all orders
    const allOrdersData = [
      ...schoolOrdersData,
      ...adOrdersData,
      ...stationeryOrdersData
    ];
    
    return {
      all: allOrdersData,
      school: schoolOrdersData,
      ad: adOrdersData,
      stationery: stationeryOrdersData
    };
  };

  // Add order type selection handler
  const handleOrderTypeSelect = (orderType) => {
    setSelectedOrderType(orderType);
    
    // Reset form data when changing order types
    setFormData({
      organization_id: '',
      business_number: '',
      business_name: '',
      business_type: '',
      orderType: '',
      productType: '',
      notes: '',
      designNotes: '',
      // Stationery order fields
      quantity: '',
      size: '',
      stock: '',
      finish: '',
      price: '',
      freightPrice: '',
      additionalArtworkHours: '',
      margin: '',
      artworkFee: '',
      setupFee: '',
      gst: '',
      discountType: '$',
      discountAmount: '',
      totalPrice: '',
      audiences: [{
        organization_id: '',
        channel: '',
        adType: '',
        startDate: '',
        endDate: '',
        price: '',
        discount: '0',
        discountType: '$', // Default to dollar discount
        eLinkType: 'None',
        eLinkContent: ''
      }]
    });
  };
  
  // Handle going back to order type selection
  const handleBackToOrderTypeSelection = () => {
    setSelectedOrderType(null);
  };

  // Create a specific function for submitting stationery orders to the correct endpoint
  const createStationeryOrder = async (formData) => {
    try {
      // Map field names to match server expectations
      const data = { 
        orderType: 'stationery',
        organization_id: formData.organization_id || null,
        business_name: formData.business_name || '',
        business_number: formData.business_number || '',
        business_type: formData.business_type || '',
        productType: formData.productType || '',
        quantity: formData.quantity || null,
        size: formData.size || '',
        sides: formData.sides || '',
        stock: formData.stock || '',
        finish: formData.finish || '',
        price: parseFloat(formData.price) || 0,
        freightPrice: parseFloat(formData.freightPrice) || 0,
        additionalArtworkHours: parseFloat(formData.additionalArtworkHours) || 0,
        margin: parseFloat(formData.margin) || 0,
        artworkFee: parseFloat(formData.artworkFee) || 0,
        setupFee: parseFloat(formData.setupFee) || 0,
        gst: parseFloat(formData.gst) || 0,
        discountType: formData.discountType || '$',
        discountAmount: parseFloat(formData.discountAmount) || 0,
        totalPrice: parseFloat(formData.totalPrice) || 0,
        notes: formData.notes || ''
      };
      
      // Get token and CSRF token for the request
      const authToken = await getToken();
      
      // Import CSRF protection
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrfToken = await fetchCSRFToken();
      
      const response = await axios.post(
        `${config.apiUrl}/api/stationery-orders`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'X-CSRF-Token': csrfToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating stationery order:', error);
      throw error;
    }
  };

  // Create a specific function for submitting ad orders to the correct endpoint
  const createAdOrder = async (formData) => {
    try {
      // Extract temp attachments before processing order data
      const { tempAttachments, ...orderDataWithoutAttachments } = formData;
      
      // Map field names to match server expectations
      const data = { 
        orderType: 'ad', // Explicitly set order type to 'ad'
        
        // Main order fields - map businessNumber to organization
        organization: orderDataWithoutAttachments.organization_id || parseInt(orderDataWithoutAttachments.businessNumber) || parseInt(orderDataWithoutAttachments.business_number) || null,
        productType: orderDataWithoutAttachments.productType || '',
        notes: orderDataWithoutAttachments.notes || '',
        designNotes: orderDataWithoutAttachments.designNotes || '',
        customerNotes: orderDataWithoutAttachments.customerNotes || '',
        totalPrice: orderDataWithoutAttachments.totalPrice || 0,
        status: orderDataWithoutAttachments.status || 'New', // Use status from form with default of 'New'

        // Invoice settings
        invoiceLater: orderDataWithoutAttachments.invoiceLater || false,
        invoiceDate: orderDataWithoutAttachments.invoiceDate || null,
        
        // Audiences using exact field names from database
        audiences: orderDataWithoutAttachments.audiences.map(audience => {
          // Remove any order_id or orderId or id as they will be set by the server
          const { order_id, orderId, id, ...rest } = audience;
          
          // Ensure each audience has its own organization ID (school)
          if (!audience.organization_id) {
            console.warn('Audience missing organization_id:', audience);
            throw new Error('Each audience must have a target organization (school) selected');
          }
          
          return {
            organization: audience.organization_id, // Use ONLY the audience's organization (school), not the advertiser
            channel: audience.channel || '',
            adType: audience.adType || '',  // Add adType field
            startDate: audience.startDate || new Date().toISOString().split('T')[0],
            endDate: audience.endDate || new Date().toISOString().split('T')[0],
            price: parseFloat(audience.price) || 0,
            discount: parseFloat(audience.discount) || 0,
            eLinkType: audience.eLinkType || '',
            eLinkContent: audience.eLinkContent || ''
          };
        })
      };
      
      // Get token and CSRF token for the request
      const authToken = await getToken();
      
      // Import CSRF protection
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrfToken = await fetchCSRFToken();
      
      // Use the specific ad-orders endpoint instead of general orders endpoint
      const response = await axios.post(
        `${config.apiUrl}/api/ad-orders`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'X-CSRF-Token': csrfToken
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating ad order:', error);
      throw error;
    }
  };

  // Update handleSubmit to use the new function
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return; // Don't close dialog if validation fails
    }
    
    // Force close dialog immediately at the beginning to ensure UI responsiveness
    setOpenAddDialog(false);
    
    try {
      let response;
      
      if (selectedOrderType === 'school') {
        // Use the dedicated school order creation function
        response = await createSchoolOrder(formData);
        
        // Check for organization update issues and notify user if needed
        if (!response.orgUpdateSuccess) {
          console.warn('Organisation update had issues:', response.orgUpdateError);
          setSnackbar({
            open: true,
            message: 'School order created, but organization update may be pending. The order will appear shortly.',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'School order created successfully',
            severity: 'success'
          });
        }
        
        // Reset selected order type
        setSelectedOrderType(null);
        
        // Use a delay before fetching to ensure database operations complete
        setTimeout(() => {
          fetchOrders();
        }, 1000);
      } else if (selectedOrderType === 'ad') {
        // Use the dedicated ad order creation function
        response = await createAdOrder(formData);
        const orderId = response.data?.id;
        
        // Handle attachments if any exist
        if (formData.tempAttachments && formData.tempAttachments.length > 0 && orderId) {
          try {
            setSnackbar({
              open: true,
              message: `Ad order created! Uploading ${formData.tempAttachments.length} attachments...`,
              severity: 'info'
            });

            await uploadTempAttachments(orderId, formData.tempAttachments, getToken);
            
            setSnackbar({
              open: true,
              message: `Ad order created successfully with ${formData.tempAttachments.length} attachments!`,
              severity: 'success'
            });
          } catch (attachmentError) {
            console.error('Error uploading attachments:', attachmentError);
            setSnackbar({
              open: true,
              message: `Ad order created but failed to upload attachments: ${attachmentError.message}`,
              severity: 'warning'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: 'Ad order created successfully',
            severity: 'success'
          });
        }
        
        setSelectedOrderType(null);
        
        // Refresh the orders list
        fetchOrders();
      } else if (selectedOrderType === 'stationery') {
        // Use the dedicated stationery order creation function
        response = await createStationeryOrder(formData);
        const orderId = response.data?.id;
        
        // Handle attachments if any exist
        if (formData.tempAttachments && formData.tempAttachments.length > 0 && orderId) {
          try {
            setSnackbar({
              open: true,
              message: `Stationery order created! Uploading ${formData.tempAttachments.length} attachments...`,
              severity: 'info'
            });

            await uploadTempAttachments(orderId, formData.tempAttachments, getToken);
            
            setSnackbar({
              open: true,
              message: `Stationery order created successfully with ${formData.tempAttachments.length} attachments!`,
              severity: 'success'
            });
          } catch (attachmentError) {
            console.error('Error uploading attachments:', attachmentError);
            setSnackbar({
              open: true,
              message: `Stationery order created but failed to upload attachments: ${attachmentError.message}`,
              severity: 'warning'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: 'Stationery order created successfully',
            severity: 'success'
          });
        }
        
        setSelectedOrderType(null);
        
        // Refresh the orders list
        fetchOrders();
      } else if (selectedOrderType === 'update') {
        // Create update order
        const authToken = await getToken();
        response = await axios.post(`${config.apiUrl}/api/update-orders`, formData, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        const orderId = response.data?.data?.id;
        
        // Handle attachments if any exist
        if (formData.tempAttachments && formData.tempAttachments.length > 0 && orderId) {
          try {
            setSnackbar({
              open: true,
              message: `Update order created! Uploading ${formData.tempAttachments.length} attachments...`,
              severity: 'info'
            });

            await uploadTempAttachments(orderId, formData.tempAttachments, getToken);
            
            setSnackbar({
              open: true,
              message: `Update order created successfully with ${formData.tempAttachments.length} attachments!`,
              severity: 'success'
            });
          } catch (attachmentError) {
            console.error('Error uploading attachments:', attachmentError);
            setSnackbar({
              open: true,
              message: `Update order created but failed to upload attachments: ${attachmentError.message}`,
              severity: 'warning'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: 'Update order created successfully',
            severity: 'success'
          });
        }
        
        setSelectedOrderType(null);
        
        // Refresh the orders list
        fetchOrders();
      } else {
        // Handle other order types here
        console.error('Unsupported order type:', selectedOrderType);
        setSnackbar({
          open: true,
          message: `Unsupported order type: ${selectedOrderType}`,
          severity: 'error'
        });
        return;
      }
      
      // Reset form data
      setFormData({
        organization_id: '',
        business_number: '',
        business_name: '',
        business_type: '',
        orderType: '',
        productType: '',
        notes: '',
        designNotes: '',
        // Stationery order fields
        quantity: '',
        size: '',
        stock: '',
        finish: '',
        price: '',
        freightPrice: '',
        additionalArtworkHours: '',
        margin: '',
        artworkFee: '',
        setupFee: '',
        gst: '',
        discountType: '$',
        discountAmount: '',
        totalPrice: '',
        audiences: [{
          organization_id: '',
          channel: '',
          adType: '',
          startDate: '',
          endDate: '',
          price: '',
          discount: '0',
          discountType: '$', // Default to dollar discount
          eLinkType: 'None',
          eLinkContent: ''
        }]
      });
      
    } catch (error) {
      console.error('Error creating order:', error);
      setSnackbar({
        open: true,
        message: `Error creating order: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Helper function to check if addon duration requirements are met for UI validation
  const checkAddonDurationRequirements = (audiences = []) => {
    for (const audience of audiences) {
      if (audience.addons && audience.addons.topSpot) {
        if (!audience.startDate || !audience.endDate) {
          return false; // Missing dates
        }
        
        const startDate = new Date(audience.startDate);
        const endDate = new Date(audience.endDate);
        const diffTime = Math.abs(endDate - startDate);
        const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        
        if (diffWeeks < 12) {
          return false; // Top Spot requires minimum 12 weeks
        }
      }
      
      // Add other addon validations here as needed
      // if (audience.addons.someOtherAddon && someCondition) { return false; }
    }
    return true; // All addon requirements met
  };

  const validateForm = () => {
    // Check if it's an ad order
    if (selectedOrderType === 'ad') {
      // Validate that advertiser organization is selected
      if (!formData.organization_id) {
        setSnackbar({
          open: true,
          message: 'Please select an advertiser organization',
          severity: 'error'
        });
        return false;
      }
      
      // Validate that all audiences have target organizations (schools) selected
      if (!formData.audiences || formData.audiences.length === 0) {
        setSnackbar({
          open: true,
          message: 'Please add at least one audience',
          severity: 'error'
        });
        return false;
      }
      
      for (let i = 0; i < formData.audiences.length; i++) {
        const audience = formData.audiences[i];
        if (!audience.organization_id) {
          setSnackbar({
            open: true,
            message: `Please select a target organization (school) for Audience ${i + 1}`,
            severity: 'error'
          });
          return false;
        }
        if (!audience.channel) {
          setSnackbar({
            open: true,
            message: `Please select a channel for Audience ${i + 1}`,
            severity: 'error'
          });
          return false;
        }
        if (!audience.adType) {
          setSnackbar({
            open: true,
            message: `Please select an ad type for Audience ${i + 1}`,
            severity: 'error'
          });
          return false;
        }
        
        // Validate addon duration requirements
        if (audience.addons && audience.addons.topSpot) {
          if (!audience.startDate || !audience.endDate) {
            setSnackbar({
              open: true,
              message: `Please set start and end dates for Audience ${i + 1} with Top Spot addon`,
              severity: 'error'
            });
            return false;
          }
          
          const startDate = new Date(audience.startDate);
          const endDate = new Date(audience.endDate);
          const diffTime = Math.abs(endDate - startDate);
          const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
          
          if (diffWeeks < 12) {
            setSnackbar({
              open: true,
              message: `Top Spot addon requires a minimum of 12 weeks. Audience ${i + 1} has ${diffWeeks} weeks. Please extend the duration.`,
              severity: 'error'
            });
            return false;
          }
        }
      }
    } else if (selectedOrderType === 'stationery') {
      // Validate basic stationery order fields
      if (!formData.business_name) {
        setSnackbar({
          open: true,
          message: 'Please enter a business name',
          severity: 'error'
        });
        return false;
      }
      
      if (!formData.productType) {
        setSnackbar({
          open: true,
          message: 'Please enter a product type',
          severity: 'error'
        });
        return false;
      }
      
      if (!formData.quantity || formData.quantity <= 0) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid quantity',
          severity: 'error'
        });
        return false;
      }
      
      if (!formData.price || formData.price <= 0) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid price',
          severity: 'error'
        });
        return false;
      }
    }
    
    return true;
  };

  const handleCreateOrder = () => {
    setOpenAddDialog(true);
  };

  // Handle opening notes dialog for any entity
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      notes: existingNotes
    });
  };

  // Handle closing notes dialog
  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (entity, entityType) => {
    // Count database notes
    let dbNoteCount = 0;
    if (entityType === 'order') {
      if (entity.notes) dbNoteCount++;
      if (entity.designNotes) dbNoteCount++;
    }
    
    // Count localStorage notes
    // For follow-up entities, use the sourceId to match the original enquiry's localStorage notes
    const entityId = entity.sourceId || entity.id;
    const storageKey = `notes_${entityType}_${entityId}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    const totalCount = dbNoteCount + storageNotes.length;
    
    return totalCount;
  };

  // Handle notes update from the dialog - update local state
  const handleNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    
    // Update the relevant local state to trigger UI refresh
    if (notesDialog.entityType === 'order') {
      setTabOrders(prev => ({
        ...prev,
        all: prev.all.map(order => 
          order.id.toString() === notesDialog.entityId 
            ? { 
                ...order, 
                notes: order.notes || 'Has notes',
                hasLocalStorageNotes: true
              }
            : order
        ),
        ad: prev.ad.map(order => 
          order.id.toString() === notesDialog.entityId 
            ? { 
                ...order, 
                notes: order.notes || 'Has notes',
                hasLocalStorageNotes: true
              }
            : order
        ),
        school: prev.school.map(order => 
          order.id.toString() === notesDialog.entityId 
            ? { 
                ...order, 
                notes: order.notes || 'Has notes',
                hasLocalStorageNotes: true
              }
            : order
        )
      }));
    }
    
    setSnackbar({ 
      open: true, 
      message: 'Note added successfully!', 
      severity: 'success' 
    });
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await fetchOrders();
      } catch (error) {
        // Silently handle reload error
      }
    }
  };

  // Add a function to handle order clicks from any tab
  const handleTabOrderClick = (order) => {
    
    // Ensure the order has a valid ID 
    if (!order.id) {
      // Try to find a valid ID from other properties
      const possibleId = order.orderId || order.schoolOrderId;
      
      if (possibleId) {
        order = { ...order, id: possibleId };
      } else {
        console.error('Cannot open order details: Missing order ID', order);
        setSnackbar({
          open: true,
          message: 'Error: Cannot view order details without a valid ID',
          severity: 'error'
        });
        return;
      }
    }
    
    // For school orders, use the school-orders endpoint to get full details
    if (order.orderType === 'school') {
      const fetchFullSchoolOrderDetails = async () => {
        try {
          // Get token for the request
          const authToken = await getToken();
          
          const response = await axios.get(
            `${config.apiUrl}/api/school-orders/${order.id}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`
              }
            }
          );
          
          if (response.data && response.data.success) {
            setSelectedOrder({
              ...response.data.data,
              orderType: 'school'
            });
            setOpenDetailDialog(true);
          } else {
            console.error('Failed to get school order details:', response.data);
            setSnackbar({
              open: true,
              message: 'Error loading school order details',
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('Error fetching school order details:', error);
          // Fall back to using the order directly
          setSelectedOrder(order);
          setOpenDetailDialog(true);
        }
      };
      
      fetchFullSchoolOrderDetails();
    } 
    // For ad orders, use the ad-orders endpoint to get full details
    else if (order.orderType === 'ad') {
      const fetchFullAdOrderDetails = async () => {
        try {
          // Get token for the request
          const authToken = await getToken();
          
          const response = await axios.get(
            `${config.apiUrl}/api/ad-orders/${order.id}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`
              }
            }
          );
          
          if (response.data && response.data.success) {
            setSelectedOrder({
              ...response.data.data,
              orderType: 'ad'
            });
            setOpenDetailDialog(true);
          } else {
            console.error('Failed to get ad order details:', response.data);
            // Fall back to regular orders endpoint
            handleOrderClick(order);
          }
        } catch (error) {
          console.error('Error fetching ad order details:', error);
          // Fall back to regular orders endpoint
          handleOrderClick(order);
        }
      };
      
      fetchFullAdOrderDetails();
    }
    else {
      // For non-school/non-ad orders, use the handleOrderClick method which uses the general orders endpoint
      handleOrderClick(order);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const orderGroups = groupOrdersByStatus();

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
      <PageHeader 
        title="Orders Management" 
        subtitle="View and manage all orders"
        action={
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreateOrder}
          >
            New Order
          </Button>
        }
      />
      
      <Box sx={{ mt: 3 }}>
        <OrdersTabs 
          schoolOrders={schoolOrders}
          stationeryOrders={stationeryOrders}
          advertiserOrders={advertiserOrders}
          updateOrders={updateOrders}
          loading={loading}
          error={error}
          onOrderClick={handleTabOrderClick}
          getTotalNoteCount={getTotalNoteCount}
          handleOpenNotesDialog={handleOpenNotesDialog}
        />
      </Box>

      {/* Add Order Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => {
          setOpenAddDialog(false);
          setSelectedOrderType(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedOrderType ? (
            <Box display="flex" alignItems="center">
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBackToOrderTypeSelection}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              {selectedOrderType === 'advertising' && 'New Advertising Order'}
              {selectedOrderType === 'school' && 'New School Order'}
              {selectedOrderType === 'stationery' && 'New Stationery Order'}
            </Box>
          ) : (
            'Add New Order'
          )}
        </DialogTitle>
        <DialogContent>
          {!selectedOrderType ? (
            <OrderTypeSelector onSelect={handleOrderTypeSelect} />
          ) : selectedOrderType === 'ad' ? (
            <AdvertisingOrderForm 
              formData={formData} 
              setFormData={setFormData} 
              organizations={organizations}
              handleAudienceChange={handleAudienceChange}
              addAudience={addAudience}
              removeAudience={removeAudience}
              onTopSpotConflictsChange={setTopSpotConflicts}
            />
          ) : selectedOrderType === 'school' ? (
            <SchoolOrderForm 
              formData={formData} 
              setFormData={setFormData} 
              organizations={organizations} 
            />
          ) : selectedOrderType === 'stationery' ? (
            <StationeryOrderForm 
              formData={formData} 
              setFormData={setFormData} 
              organizations={organizations} 
            />
          ) : selectedOrderType === 'update' ? (
            <UpdateOrderForm 
              formData={formData} 
              setFormData={setFormData} 
              organizations={organizations} 
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAddDialog(false);
            setSelectedOrderType(null);
          }}>
            Cancel
          </Button>
          {selectedOrderType && (
            <Button 
              onClick={(e) => {
                // Force close the dialog immediately before processing
                setOpenAddDialog(false);
                setSelectedOrderType(null);
                
                // Then handle the actual submission
                handleSubmit(e);
                
                // Add redundant close logic in case the async operation doesn't close the dialog
                setTimeout(() => {
                  if (openAddDialog) {
                    setOpenAddDialog(false);
                    setSelectedOrderType(null);
                  }
                }, 2000);
              }}
              variant="contained" 
              disabled={
                selectedOrderType === 'ad' ? 
                  (!formData.organization_id || !formData.productType || 
                  !formData.audiences[0].organization_id || !formData.audiences[0].channel || 
                  !formData.audiences[0].startDate || !formData.audiences[0].endDate || 
                  !formData.audiences[0].price || !checkAddonDurationRequirements(formData.audiences) ||
                  !checkOrderTopSpotRequirements(formData.audiences, topSpotConflicts)) : 
                  selectedOrderType === 'school' ?
                  (!formData.organization_id || 
                   !formData.firstName || !formData.lastName || 
                   !formData.numberOfFamilies || 
                   !formData.issuesPerTerm) :
                  selectedOrderType === 'stationery' ?
                  (!formData.business_name || !formData.productType || 
                   !formData.quantity || !formData.price) :
                  false
              }
            >
              Add Order
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Archive Order Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Archive Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this order? You can restore it from the Archive page later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteOrder} color="warning">Archive</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Order Detail Dialog */}
      <OrderDetail
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        order={selectedOrder}
        onUpdate={handleOrderUpdate}
      />

      {/* Notes Dialog - Order Workflow Version */}
      <OrderWorkflowNotesDialog
        open={notesDialog.open}
        onClose={handleCloseNotesDialog}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
        onNotesUpdate={handleNotesUpdate}
      />
    </Container>
  );
}

export default Orders; 
